import React from "react";
import { type ReactNode } from "react";
import { Text, useBreakpointValue, type SpaceProps } from "@chakra-ui/react";
import { TextTitle } from "ui/text";
import { AuraPlate } from "ui/plates";
import { type BrandColorVariants } from "theme/colors";
import { NavatticDemoModal } from "../demo-embed";
import { BookDemoButton } from "../event-tracking";

export type FooterCTAProps = {
  variant: keyof BrandColorVariants;
  title?: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
} & SpaceProps;

export function FooterCTA({ title, description, actions, variant }: FooterCTAProps) {
  // Only show the navattic demo for `lg`+ desktop views, otherwise show a demo request
  const defaultTitle = useBreakpointValue({
    base: (
      <Text textStyle={{ base: "headline2", md: "headline1" }}>
        Start syncing data
        <br />
        in just minutes
      </Text>
    ),
    lg: "Take a virtual tour",
  });
  const defaultDescription = useBreakpointValue({
    base: "",
    lg: "Check out this 5 minute interactive demo to learn how you can sync customer data to any business tool.",
  });
  // Avoid useBreakpointValue for this to prevent re-rendering the demo modal at breakpoints, which causes it to close if already opened
  const defaultActions = (
    <>
      <BookDemoButton display={{ base: "block", lg: "none" }} />
      <NavatticDemoModal display={{ base: "none", lg: "block" }}>Let&apos;s go!</NavatticDemoModal>
    </>
  );

  return (
    <AuraPlate
      theme="dark"
      variant={variant}
      py="0"
      plateMaxW="section"
      platePx={{ base: "small", lg: "biggest" }}
      platePy={{ base: "biggest", md: "huge" }}
    >
      <TextTitle
        theme="dark"
        size="xl"
        align="center"
        mobileAlign="center"
        maxW="680px"
        title={title ?? defaultTitle}
        description={description ?? defaultDescription}
        actions={actions ?? defaultActions}
      />
    </AuraPlate>
  );
}
